// What agents reached for in a mailbox, and what the confidentiality guard held
// back - plus the owner's release action.
//
// Mirrors POST /mailbox/read-log and POST /mailbox/release
// (.../api/mailbox_access/). Both are scoped SERVER-side to the mailboxes the
// caller authorised, so this hook takes no identity argument: there is nothing
// the browser could pass that would widen what it sees. The period and outcome
// filters are applied in SQL too, never here - a filter over a capped page would
// drop the withheld rows it exists to show.
//
// See docs/Y2026-Q3/design-mailbox-confidentiality-guard.md sections 6.4, 6.6.
import { useMutation, useQuery, useQueryClient } from "react-query";

import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

// One thread an agent reached for, summarised over every reach in the period:
// the owner decides once per thread, not once per search that found it.
export interface MailboxReadLogThread {
  mailbox_id: string;
  gmail_thread_id: string;
  project_id: string | null;
  reaches: number;
  withheld_reaches: number;
  last_at: string;
  last_outcome: "served" | "withheld";
  // The class the latest reach was decided on: "business", "personal",
  // "unsure" (the classifier leaned business without confidence), "credential"
  // (sign-in or security mail, never releasable), "contains_secret" (work mail read
  // with its credentials taken out, never released raw), or null (nothing had judged it).
  confidentiality_class: string | null;
  reason: string | null;
  // The queries that found it, so the owner can re-run them in Gmail.
  queries: string[];
  requested_by: string[];
  tools: string[];
  subject: string | null;
  participants: string[];
  // Where the thread stands now, which can differ from when it was reached.
  current_class: string | null;
  released_here: boolean;
  // The latest reach's run, which keys the classifier's trace for it.
  run_id: string | null;
}

export interface MailboxReadLogInbox {
  mailbox_id: string;
  account_email: string;
  // Threads, not reaches.
  counts: { withheld: number; served: number };
  threads: MailboxReadLogThread[];
  capped: boolean;
}

export interface MailboxReadLogResponse {
  mailboxes: MailboxReadLogInbox[];
}

export interface MailboxReadLogFilter {
  mailboxId?: string;
  outcome: "withheld" | "all";
  // null = everything.
  sinceDays: number | null;
}

export const useMailboxReadLog = (filter?: MailboxReadLogFilter) => {
  const http = useAgentsHttp();
  return useQuery<MailboxReadLogResponse, Error>([QK.mailboxReadLog, filter ?? null], () =>
    http<MailboxReadLogResponse>(
      "/mailbox/read-log",
      filter
        ? {
            mailbox_id: filter.mailboxId,
            outcome: filter.outcome,
            since_days: filter.sinceDays,
          }
        : {},
    ),
  );
};

export interface ReleaseThreadBody {
  mailbox_id: string;
  gmail_thread_id: string;
  project_id: string;
  reason?: string;
}

export interface ReleaseThreadResult {
  released: boolean;
  already: boolean;
  // Copies already on the project that the release opened, and those still held
  // because another thread in the same conversation is withheld.
  documents_opened?: number;
  documents_still_held?: number;
}

export const useReleaseThread = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<ReleaseThreadResult, Error, ReleaseThreadBody>(
    (body) => http("/mailbox/release", body),
    { onSuccess: () => void qc.invalidateQueries([QK.mailboxReadLog]) },
  );
};
